import { useState, useCallback } from "react";
import { ChartArea as TradingChartArea } from "@/components/lytick/ChartArea";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { DrawingToolRail } from "@/components/features/DrawingToolRail";
import { DrawingCursorPill } from "@/components/features/DrawingCursorPill";
import { ChartLegend } from "@/components/features/ChartLegend";
import { ActiveToolBreadcrumb } from "@/components/features/ActiveToolBreadcrumb";
import { ReplayTransportBar } from "@/components/layout/ReplayTransportBar";
import { IndicatorPanel } from "@/components/features/IndicatorPanel";
import { useDesktopPanelStore } from "@/stores/panelStore";
import { useReplayStore } from "@/stores/replayStore";
import { useSettingsStore } from "@/stores/settingsStore";
import { useCsvImport } from "@/hooks/useCsvImport";

export function ChartArea() {
  const indicatorsOpen = useDesktopPanelStore((state) => state.indicatorsOpen);
  const closeIndicators = useDesktopPanelStore((state) => state.closeIndicators);
  const isReplayMode = useReplayStore((state) => state.isReplayMode);
  const isReplayActive = useReplayStore((state) => state.isActive);
  const showWatermark = useSettingsStore((s) => s.chartAppearance.showWatermark ?? true);
  const showOHLC = useSettingsStore((s) => s.showOHLC);
  const { importFile } = useCsvImport();
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer.types.includes("Files")) {
      e.preventDefault();
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) void importFile(file);
  }, [importFile]);

  return (
    <section
      id="desktop-chart-surface"
      className="relative flex h-full w-full overflow-hidden focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent-primary)]"
      style={{ backgroundColor: "var(--bg-base)" }}
      tabIndex={0}
      aria-label="Chart workspace"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div
          className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-2 pointer-events-none"
          style={{
            backgroundColor: "color-mix(in srgb, var(--accent-primary) 10%, var(--bg-base) 90%)",
            border: "2px dashed var(--accent-primary)",
          }}
        >
          <span className="text-sm font-semibold" style={{ color: "var(--accent-primary)" }}>
            Drop CSV to import
          </span>
        </div>
      )}
      <IndicatorPanel open={indicatorsOpen} onClose={closeIndicators} />

      <div className="relative flex h-full w-full flex-col">
        {/* Desktop: rail is in-flow so the line-tools submenu pushes the chart right */}
        <div className="relative hidden h-full w-full lg:flex">
          <DrawingToolRail />
          {/* Chart area: fills remaining flex space; overlays are chart-relative */}
          <div className="relative flex flex-col flex-1 min-w-0 h-full">
            <ErrorBoundary
              fallback={({ reset }) => (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    color: "var(--text-secondary)",
                    fontSize: "13px",
                  }}
                >
                  <span>Chart failed to render.</span>
                  <button
                    onClick={reset}
                    style={{
                      fontSize: "12px",
                      padding: "4px 12px",
                      borderRadius: "6px",
                      border: "1px solid var(--border-default)",
                      background: "var(--bg-elevated)",
                      color: "var(--text-primary)",
                      cursor: "pointer",
                    }}
                  >
                    Reset
                  </button>
                </div>
              )}
            >
              <TradingChartArea desktopMode interactive />
            </ErrorBoundary>
            {showOHLC && <ChartLegend />}
            <DrawingCursorPill />
            <ActiveToolBreadcrumb />
            {(isReplayMode || isReplayActive) && <ReplayTransportBar />}
            {showWatermark && (
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                  userSelect: "none",
                  zIndex: 1,
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-brand)",
                    fontSize: "clamp(80px, 16vw, 200px)",
                    fontWeight: 500,
                    letterSpacing: "-0.03em",
                    color: "var(--text-primary)",
                    opacity: 0.035,
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  LYTICK
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

    </section>
  );
}
